"use client";

import * as React from "react";

import { isEqual } from "lodash";
import { ChevronsDownIcon,
  GhostIcon,
} from "lucide-react";
import { usePathname } from "next/navigation";
import {
  useInfiniteHits,
  useInstantSearchContext,
  useSearchBox,
} from "react-instantsearch";
import { useInView } from "react-intersection-observer";

import { getEnv } from "@/shared.utils/env";
import { approxNumber, numFormat } from "@/shared.utils/number-format";
import { Breadcrumbs } from "@/shared-web.ui/ui.breadcrumbs";
import { Button } from "@/shared-web.ui/ui.button";
import { Heading } from "@/shared-web.ui/ui.heading";
import { Skeleton } from "@/shared-web.ui/ui.skeleton";
import { Text } from "@/shared-web.ui/ui.text";
import { cn } from "@/shared-web.ui/util.cn";
import {
  type GetCategoryDataReturn,
} from "@/stf.data/request.product-private";
import { type ProductListingSummary } from "@/stf.data/schema";
import { LayoutProductList } from "@/stf.ui/layout.product-list";
import { AppContainer } from "@/stf.ui/ui.app-container";
import { CardGeneric, CardSkeleton } from "@/stf.ui/ui.product-card-generic";

import { ProductFacet } from "../feat.product-facet";
import { ProductListContext, useProductListContext } from "../feat.product-list-context";
import { ProductInstantSearch, VirtualWidget } from "../feat.product-list-instant-search";
import { ProductSearch } from "../feat.product-search";
import { ProductSort } from "../feat.product-sort";

const ScreenSkeleton = () => {
  return (
    <>
      <LayoutProductList.Header>
        <LayoutProductList.HeaderCategory>
          <Skeleton className="h-5 w-14" />
          <Skeleton>
            <Heading
              as="h1"
              size="5"
            >
              Skeleton
            </Heading>
          </Skeleton>
        </LayoutProductList.HeaderCategory>
        <LayoutProductList.HeaderFacet>
          <Skeleton>
            <Button variant="inline-ghost">
              Filter
            </Button>
          </Skeleton>
          <Skeleton>
            <Button variant="inline-ghost">
              Sort By
            </Button>
          </Skeleton>
        </LayoutProductList.HeaderFacet>
      </LayoutProductList.Header>
      <LayoutProductList.Body>
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </LayoutProductList.Body>
    </>
  );
};

const ProductListBody = () => {
  const { items } = useInfiniteHits<ProductListingSummary>();
  const { status } = useInstantSearchContext();
  const { query, clear } = useSearchBox();

  if (status === "idle" && items.length === 0) {
    return (
      <div
        className={`
          flex flex-col items-center justify-start gap-6
          px-4 py-20
          sm:px-8
        `}
      >
        <GhostIcon
          strokeWidth="1"
          className="size-64 text-gray-7"
        />
        <Text weight="light" size="7" color="gray">
          No product found for
          {` "${query}"`}
        </Text>
        {query
          ? (
              <Button
                size="3"
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  clear();
                }}
              >
                Clear search
              </Button>
            )
          : null}
      </div>
    );
  }

  return (
    <LayoutProductList.Body>
      <LayoutProductList.BodyColumn>
        {items
          ? items.map((hit, index) => {
            if (!(index % 2)) {
              return (
                <CardGeneric
                  {...hit}
                  key={hit.productId}
                />
              );
            }
            return null;
          })
          : null}

        {status === "loading" && (
          <>
            <CardSkeleton />
            <CardSkeleton />
          </>
        )}
      </LayoutProductList.BodyColumn>
      <LayoutProductList.BodyColumn>
        {items
          ? items.map((hit, index) => {
            if ((index % 2)) {
              return (
                <CardGeneric
                  {...hit}
                  key={hit.productId}
                />
              );
            }
            return null;
          })
          : null}

        {status === "loading" && (
          <>
            <CardSkeleton />
            <CardSkeleton />
          </>
        )}
      </LayoutProductList.BodyColumn>
    </LayoutProductList.Body>
  );
};

interface ProductListProps extends GetCategoryDataReturn {}
const ProductList = React.memo((props: ProductListProps) => {
  const {
    categoryPath,
  } = props;

  const {
    ref: bottomRef,
    inView: bottomInView,
  } = useInView();

  const { status } = useInstantSearchContext();
  const { showMore, isLastPage, items, results } = useInfiniteHits<ProductListingSummary>();

  React.useEffect(() => {
    if (bottomInView) {
      showMore();
    }
  }, [bottomInView]);

  // if (isLoading) {
  //   return <ScreenSkeleton />;
  // }

  // if (!data) {
  //   return <p>Not found</p>;
  // }

  let hitCount: null | string = null;
  if (results?.nbHits) {
    const approxHitCount = approxNumber(results.nbHits);
    hitCount = `${numFormat(approxHitCount, { decimal: 0 })}`;
    if (approxHitCount < results.nbHits) {
      hitCount += "+";
    }
  }

  return (
    <div className="min-h-[calc(100dvh_-_72px)]">
      <LayoutProductList.Header>
        <LayoutProductList.HeaderCategory>
          <Breadcrumbs
            paths={categoryPath}
            size="1"
          />
          <Heading
            as="h1"
            size="5"
            className="mt-2"
          >
            {categoryPath.at(-1)?.label}
          </Heading>
        </LayoutProductList.HeaderCategory>
        <LayoutProductList.HeaderRefinement>
          <LayoutProductList.HeaderFacet>
            <ProductFacet />
            <ProductSort />
          </LayoutProductList.HeaderFacet>
          <ProductSearch />
          {results?.nbHits
            ? (
                <Text size="1" semanticColor="accent">
                  {`${hitCount} Items`}
                </Text>
              )
            : null}
        </LayoutProductList.HeaderRefinement>
      </LayoutProductList.Header>

      {/* <AppContainer>
        <Button className="mb-8 w-full" color="gray" size="3" variant="ghost">
          <ChevronsUpIcon className="size-4" />
          Load More
        </Button>
      </AppContainer> */}

      <ProductListBody />

      <AppContainer>
        <Button
          className={cn(
            "my-8 w-full",
            (status === "loading") && "hidden",
            (isLastPage) && "hidden",
          )}
          color="gray"
          size="3"
          variant="ghost"
          ref={bottomRef}
        >
          <ChevronsDownIcon className="size-4" />
          Load More
        </Button>
      </AppContainer>

      <AppContainer
        className={cn(
          (!isLastPage || items.length === 0) && "hidden",
        )}
      >
        <div
          className={`
            flex flex-col items-center justify-center gap-4
            p-20
          `}
        >

          <Text size="2" color="gray">
            :)
            You&apos;ve reached the end of the list.
          </Text>
        </div>
      </AppContainer>

    </div>
  );
}, isEqual);
ProductList.displayName = "ProductList";

interface ScreenProductListProps extends ProductListProps {}
const ActualProductList = React.memo((props: ProductListProps) => {
  const {
    categoryAttribute,
    categoryVariant,
  } = props;

  const hierarchicalMenuAttributes = React.useMemo(() => {
    return [
      "categoryHierarchySlug.lvl0",
      "categoryHierarchySlug.lvl1",
      "categoryHierarchySlug.lvl2",
    ];
  }, []);
  const productIndex = getEnv(
    process.env.NEXT_PUBLIC_SEARCH_INDEX_PRODUCT,
    "NEXT_PUBLIC_SEARCH_INDEX_PRODUCT",
  );
  const sortByItems = React.useMemo(() => {
    return [
      {
        value: productIndex,
        label: "Best Match",
        slug: "best-match",
      },
      {
        value: `${productIndex}_price_asc`,
        label: "Price Low to High",
        slug: "price-asc",
      },
      {
        value: `${productIndex}_price_desc`,
        label: "Price High to Low",
        slug: "price-desc",
      },
    ];
  }, []);
  const fixedRange = React.useMemo(() => {
    return [
      {
        searchSlug: "variantSku.promoPrice",
        urlSlug: "price",
      },
    ];
  }, []);

  return (
    <ProductInstantSearch
      hierarchicalMenuAttributes={hierarchicalMenuAttributes}
      sortByItems={sortByItems}
      categoryAttribute={categoryAttribute}
      categoryVariant={categoryVariant}
      fixedRange={fixedRange}
    >
      <VirtualWidget
        hierarchicalMenuAttributes={hierarchicalMenuAttributes}
        sortByItems={sortByItems}
        categoryAttribute={categoryAttribute}
        categoryVariant={categoryVariant}
        fixedRange={fixedRange}
      />
      <ProductListContext.Provider
        value={{
          hierarchicalMenuAttributes,
          sortByItems,
          categoryAttribute,
          categoryVariant,
          fixedRange,
        }}
      >
        <ProductList
          {...props}
        />
      </ProductListContext.Provider>
    </ProductInstantSearch>
  );
}, isEqual);
ActualProductList.displayName = "ActualProductList";

const ScreenProductList = (props: ScreenProductListProps) => {
  const pathname = usePathname();

  /* this ProductList screen depends on url,
  * when url change from nextjs intercept route
  * and change url to something we didn't expect
  * while component still mount it'll try to interpret unknown url
  * that will cause an error, "if" statement here is to prevent that
  * ---
  * Matches exactly '/search' || '/en/search'
  * Matches '/shop/' || '/en/shop' followed by a word or hyphen or number (e.g., '/shop/category')
  * Optionally matches another segment after '/shop/' (e.g., '/shop/category/sub-category')
  * Doesn't allow extra segments beyond two after '/shop/'
  */
  if (/^(\/[a-z]{2})?(\/search|\/shop\/[a-z0-9-]+(?:\/[a-z0-9-]+)?)$/u.test(pathname)) {
    return <ActualProductList {...props} />;
  }
  return <ScreenSkeleton />;
};

export { ScreenProductList, ScreenSkeleton };
